import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components';
import I18n from 'i18n-js';
import axios from 'axios';
import { useModalManager } from '@/components/common/Modal';
import { UnitType } from '@/components/shared/NewDashboard/types';
import cn from '@/utils/cn';
import { buttonFocusRing } from '@/components/ui/ui.constants';
import * as Routes from '@/modules/routes';
import TableOfContentsModal from './TableOfContentsModal';
import TableOfContents from './TableOfContents';

interface TableOfContentsButtonProps {
  programId: number;
  renderInModal?: boolean;
  units?: UnitType[];
  lessonId?: number;
}

const TableOfContentsButton = ({
  lessonId = null,
  programId,
  renderInModal = false,
  units: initialUnits = [],
}: TableOfContentsButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [units, setUnits] = useState<any[]>(initialUnits);
  const [loading, setLoading] = useState(true);
  const modalManager = useModalManager();

  useEffect(() => {
    const route = Routes.plato_api_program_units_path(programId);

    axios.get(route)
      .then((response) => {
        setUnits(response.data.data);
        setLoading(false);
      });
  }, [programId]);

  return (
    <DialogTrigger>
      <Button
        aria-controls={renderInModal ? 'table-of-contents-modal' : 'table-of-contents-popover'}
        aria-expanded={modalManager.isOpen || isOpen}
        aria-haspopup="dialog"
        className={cn(
          'tw-bg-white tw-border tw-border-tci-purple tw-rounded tw-cursor-pointer',
          'tw-flex tw-leading-7 tw-text-tci-purple tw-font-[inherit] tw-text-lg',
          'tw-h-10 tw-items-center tw-justify-center tw-px-2.5 tw-py-2',
          'hover:tw-bg-tci-purple hover:tw-text-white',
          'active:tw-bg-dark-purple active:tw-text-white',
          'focus:tw-outline-none',
          buttonFocusRing,
        )}
        isDisabled={loading}
        onPress={() => (renderInModal ? modalManager.open() : setIsOpen(prev => !prev))}
      >
        <i aria-hidden className="fa fa-icon fa-list-ul tw-text-xl tw-mr-2" />
        <div className="tw-flex tw-items-center tw-ml-1.5 tw-whitespace-nowrap">
          {I18n.t('table_of_contents')}
        </div>
      </Button>

      <Popover
        className="toc-popover"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <Dialog
          className={cn(
            'tw-text-base tw-border-med-grey tw-rounded-lg tw-shadow-md',
            'tw-bg-white tw-w-[calc(100vw-1rem)] sm:tw-w-[43.75rem]',
          )}
          id="table-of-contents-popover"
        >
          <header
            className={cn(
              'tw-rounded-t-lg tw-text-xl tw-font-bold',
              'tw-py-2 tw-px-4 tw-text-[#555]',
            )}
          >
            {I18n.t('table_of_contents')}
            <Button
              aria-label={I18n.t('close_dialog')}
              className="tw-absolute tw-right-2 tw-text-xl tw-border-0 tw-cursor-pointer tw-text-[#555]"
              onPress={() => setIsOpen(false)}
            >
              ✕
            </Button>
          </header>
          <TableOfContents lessonId={lessonId} units={units} />
        </Dialog>
      </Popover>

      {modalManager.isOpen && (
        <TableOfContentsModal
          closeModal={modalManager.close}
          isOpen={modalManager.isOpen}
          units={units}
        />
      )}
    </DialogTrigger>
  );
};

export default TableOfContentsButton;
